/**
 * ChatBox -- real-time Firebase/Firestore chat panel.
 *
 * Sender role rendering:
 *   customer -> right-aligned, forest bubble, "You" avatar
 *   provider -> left-aligned, mint bubble,   "FE"  avatar (FinminOS Expert)
 *   admin    -> left-aligned, amber bubble,  "ADM" avatar (FinminOS team)
 *
 * Beyond plain text, two message types flow through the chat:
 *   document_request -> provider/admin asks the customer for a file; the
 *                       customer sees an "Add document" upload button.
 *   document         -> a file has been shared; renders as a downloadable card.
 */
import * as React from 'react';
import {
  Send, Loader2, AlertCircle, MessageCircle, Hourglass,
  Paperclip, FileText, Download, FileUp,
} from 'lucide-react';
import { toast } from '@finmin/ui';
import { useFirebaseChat, type ChatMessage } from '../hooks/use-firebase-chat';
import { useUploadDocument, useDownloadDocument } from '@/api/documents';
import { formatChatTime, validateDocumentFile, triggerBlobDownload } from '../utils';
import { ACCEPT_ATTR } from '../constants';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';

/**
 * Workflow states where the customer can talk to the provider.
 *
 * Before a provider is assigned (`provider_assignment_start`), the chat is
 * read-only — the customer might see admin messages but cannot send anything,
 * so we don't accidentally route notes intended for the expert into a void.
 *
 * Once the assignment completes, the chat opens for the rest of the journey.
 */
const PROVIDER_CHAT_STATES = new Set<string>([
  'provider_assignment_complete',
  'platform_clarification',
  'draft_evaluation',
  'acknowledgement_submission',
  'acknowledgement_submitted',
  'service_request_complete', // read-only, but state still counts as "open"
  'service_delivered',        // backend alias when admin completes the service
]);

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';
const AMBER  = 'hsl(var(--palette-amber,  33   100% 50%))';

// Sender role -> display config
function getSenderConfig(role: string, isCustomer: boolean) {
  if (isCustomer) {
    return {
      label:       'You',
      bubbleBg:    FOREST,
      bubbleColor: '#fff',
      avatarBg:    FOREST,
      avatarColor: '#fff',
      borderRadius: { borderBottomRightRadius: 4 },
    };
  }
  if (role === 'provider') {
    return {
      label:       'FE',
      bubbleBg:    MINT,
      bubbleColor: FOREST,
      avatarBg:    MINT,
      avatarColor: FOREST,
      borderRadius: { borderBottomLeftRadius: 4 },
    };
  }
  // admin
  return {
    label:       'ADM',
    bubbleBg:    'hsl(33 100% 50% / 0.12)',
    bubbleColor: 'hsl(var(--foreground))',
    avatarBg:    AMBER,
    avatarColor: '#fff',
    borderRadius: { borderBottomLeftRadius: 4 },
  };
}

interface Props {
  request: CustomerServiceRequestResponse;
}

export function ChatBox({ request }: Props) {
  const [draft, setDraft]   = React.useState('');
  const scrollRef           = React.useRef<HTMLDivElement>(null);
  const inputRef            = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef        = React.useRef<HTMLInputElement>(null);
  // Label to attach to the NEXT upload — set when the customer taps "Add
  // document" on a specific request; null for a proactive attach.
  const pendingLabelRef     = React.useRef<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  const ws = request.customer_workflow_state ?? '';
  const isCompleted          = ws === 'service_request_complete' || ws === 'service_delivered';
  // True only once the assignment workflow has actually completed and the
  // customer is talking to a real expert. Drives the composer lock below.
  const canChatWithProvider  = PROVIDER_CHAT_STATES.has(ws);
  const providerAssigned     = !!request.assigned_provider_id;

  const { messages, status, error, sending, sendMessage, sendDocumentMessage } = useFirebaseChat({
    requestId:  request.id,
    customerId: request.customer_id,
  });

  const { mutateAsync: uploadDoc }   = useUploadDocument();
  const { mutateAsync: downloadDoc } = useDownloadDocument();
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const composerDisabled = sending || uploading || status !== 'ready';

  async function handleSend() {
    // Defense in depth: even if the input were somehow enabled, we refuse to
    // ship a message into Firestore before the provider is assigned. The UI
    // also already swaps the composer for a waiting panel below.
    if (!draft.trim() || composerDisabled || isCompleted || !canChatWithProvider) return;
    const ok = await sendMessage(draft);
    if (ok) {
      setDraft('');
      inputRef.current?.focus();
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  /** Open the OS file picker; `label` (from a request) is attached to the upload. */
  function pickFile(label: string | null) {
    if (composerDisabled || isCompleted || !canChatWithProvider) return;
    pendingLabelRef.current = label;
    fileInputRef.current?.click();
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;

    const err = validateDocumentFile(file);
    if (err) { toast.error(err); return; }

    const label = pendingLabelRef.current ?? undefined;
    pendingLabelRef.current = null;

    setUploading(true);
    try {
      const doc = await uploadDoc({
        serviceRequestId: request.id,
        file,
        documentType:  'other',
        documentLabel: label,
      });
      await sendDocumentMessage({
        documentId: doc.id,
        fileName:   file.name,
        label,
      });
    } catch {
      toast.error(`Could not send ${file.name}. Please try again.`);
    } finally {
      setUploading(false);
      inputRef.current?.focus();
    }
  }

  async function handleDownload(msg: ChatMessage) {
    if (!msg.documentId || downloadingId) return;
    setDownloadingId(msg.id);
    try {
      const { blob, filename } = await downloadDoc({
        serviceRequestId: request.id,
        documentId:       msg.documentId,
      });
      triggerBlobDownload(blob, msg.fileName || filename);
    } catch {
      toast.error('Could not download the file. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }

  // ── Loading / error states ────────────────────────────────────────────────

  if (status === 'connecting') {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-8">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: FOREST }} />
        <p className="text-sm text-muted-foreground">Connecting to chat…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-8 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm font-medium text-foreground">Chat coming soon</p>
        <p className="text-xs text-muted-foreground max-w-xs">{error}</p>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* Role legend -- compact, sits above messages */}
      <div className="shrink-0 flex items-center gap-4 px-4 py-2 border-b border-border bg-muted/30">
        <span className="text-[10px] text-muted-foreground font-medium tracking-wide uppercase">
          In this chat:
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold"
            style={{ backgroundColor: FOREST, color: '#fff' }}
          >You</span>
          <span className="text-[10px] text-muted-foreground">You</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold"
            style={{ backgroundColor: MINT, color: FOREST }}
          >FE</span>
          <span className="text-[10px] text-muted-foreground">FinminOS Expert</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold"
            style={{ backgroundColor: AMBER, color: '#fff' }}
          >ADM</span>
          <span className="text-[10px] text-muted-foreground">FinminOS Team</span>
        </span>
      </div>

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-3">
        {messages.length === 0 && status === 'ready' && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-12">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center"
              style={{ backgroundColor: MINT }}
            >
              <MessageCircle className="h-6 w-6" style={{ color: FOREST }} />
            </div>
            <p className="text-sm font-medium text-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground max-w-xs">
              Once a provider is assigned, you can chat with your expert here.
            </p>
          </div>
        )}

        {messages.map(msg => {
          const isCustomer = msg.senderRole === 'customer';
          const cfg = getSenderConfig(msg.senderRole, isCustomer);
          const senderName = isCustomer
            ? 'You'
            : msg.senderRole === 'provider' ? 'FinminOS Expert' : 'FinminOS Team';

          return (
            <div
              key={msg.id}
              className={`flex gap-2.5 ${isCustomer ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {/* Avatar */}
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 self-end"
                style={{ backgroundColor: cfg.avatarBg, color: cfg.avatarColor }}
              >
                {cfg.label}
              </div>

              {/* Bubble */}
              <div className={`max-w-[75%] flex flex-col gap-0.5 ${isCustomer ? 'items-end' : 'items-start'}`}>
                {/* Sender name above bubble */}
                <span className="text-[10px] text-muted-foreground px-1 font-medium">
                  {senderName}
                </span>

                {msg.messageType === 'document_request' ? (
                  <DocumentRequestBubble
                    label={msg.documentLabel || msg.text}
                    canUpload={!isCustomer && canChatWithProvider && !isCompleted}
                    uploading={uploading}
                    onUpload={() => pickFile(msg.documentLabel || msg.text || null)}
                  />
                ) : msg.messageType === 'document' ? (
                  <DocumentBubble
                    msg={msg}
                    isCustomer={isCustomer}
                    cfg={cfg}
                    downloading={downloadingId === msg.id}
                    onDownload={() => void handleDownload(msg)}
                  />
                ) : (
                  <div
                    className="px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words whitespace-pre-wrap"
                    style={{
                      backgroundColor: cfg.bubbleBg,
                      color:           cfg.bubbleColor,
                      ...cfg.borderRadius,
                    }}
                  >
                    {msg.text}
                  </div>
                )}

                {msg.timestamp && (
                  <span className="text-[10px] text-muted-foreground px-1">
                    {formatChatTime(msg.timestamp)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Hidden file input shared by the attach button and request bubbles. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="sr-only"
        onChange={onFilePicked}
      />

      {/* Composer.
          Three modes, in priority order:
            1. service complete → read-only notice
            2. provider not yet assigned → waiting panel (no input)
            3. otherwise → live composer */}
      {isCompleted ? (
        <div className="shrink-0 px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            This chat is read-only — the service has been completed.
          </p>
        </div>
      ) : !canChatWithProvider ? (
        <div className="shrink-0 px-4 py-4 border-t border-border" style={{ backgroundColor: MINT }}>
          <div className="flex items-start gap-3">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.55)' }}
            >
              <Hourglass className="h-4 w-4" style={{ color: FOREST }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: FOREST }}>
                {providerAssigned
                  ? 'Connecting you to your expert…'
                  : 'Waiting for an expert to be assigned'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {providerAssigned
                  ? 'Almost there. The chat will open in a moment.'
                  : "Our team is matching you with the right CA. You'll be able to chat once they're on board."}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="shrink-0 flex items-end gap-2 px-4 py-3 border-t border-border">
          {/* Attach — proactively share a document. */}
          <button
            type="button"
            onClick={() => pickFile(null)}
            disabled={composerDisabled}
            className="h-10 w-10 rounded-xl flex items-center justify-center border border-border text-muted-foreground transition-all disabled:opacity-50 hover:bg-muted active:scale-95 shrink-0"
            aria-label="Attach a document"
            title="Attach a document"
          >
            {uploading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Paperclip className="h-4 w-4" />
            }
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type your message…"
            disabled={composerDisabled}
            className="flex-1 max-h-32 resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!draft.trim() || composerDisabled}
            className="h-10 w-10 rounded-xl flex items-center justify-center text-white transition-all disabled:opacity-50 active:scale-95 shrink-0"
            style={{ backgroundColor: FOREST }}
            aria-label="Send message"
          >
            {sending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />
            }
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------- Internal helpers --------------- */

/** A provider/admin request for a document, with an upload affordance. */
function DocumentRequestBubble({
  label,
  canUpload,
  uploading,
  onUpload,
}: {
  label: string;
  canUpload: boolean;
  uploading: boolean;
  onUpload: () => void;
}) {
  return (
    <div
      className="px-3.5 py-3 rounded-2xl text-sm leading-relaxed w-full"
      style={{ backgroundColor: MINT, color: FOREST, borderBottomLeftRadius: 4 }}
    >
      <div className="flex items-start gap-2">
        <FileUp className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <p className="font-semibold">Document requested</p>
          <p className="break-words">{label}</p>
        </div>
      </div>
      {canUpload && (
        <button
          type="button"
          onClick={onUpload}
          disabled={uploading}
          className="mt-2.5 w-full h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 text-white transition-all disabled:opacity-50 active:scale-95"
          style={{ backgroundColor: FOREST }}
        >
          {uploading
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Uploading…</>
            : <><Paperclip className="h-3.5 w-3.5" aria-hidden /> Add document</>
          }
        </button>
      )}
    </div>
  );
}

/** A shared file, rendered as a downloadable card. */
function DocumentBubble({
  msg,
  isCustomer,
  cfg,
  downloading,
  onDownload,
}: {
  msg: ChatMessage;
  isCustomer: boolean;
  cfg: ReturnType<typeof getSenderConfig>;
  downloading: boolean;
  onDownload: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onDownload}
      disabled={downloading}
      className="px-3 py-2.5 rounded-2xl text-sm text-left flex items-center gap-2.5 transition-all disabled:opacity-60 hover:brightness-95 active:scale-[0.98]"
      style={{ backgroundColor: cfg.bubbleBg, color: cfg.bubbleColor, ...cfg.borderRadius }}
    >
      <span
        className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: isCustomer ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)' }}
      >
        <FileText className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block font-medium truncate max-w-[180px]">
          {msg.fileName || msg.documentLabel || 'Document'}
        </span>
        <span className="flex items-center gap-1 text-[11px] opacity-80">
          {downloading
            ? <><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Downloading…</>
            : <><Download className="h-3 w-3" aria-hidden /> Tap to download</>
          }
        </span>
      </span>
    </button>
  );
}
